import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IApiResult } from '@common-interfaces/api';
import { environment } from '../../environments/enviroment';

interface IApi {
    /**
     * @param {string} url - Path to append to the base URL.
     * @returns {Promise<IApiResult<T>>} Resolves with an `IApiResult<T>` containing `succeeded`, `error`, and `data`.
     */
    get: <T>(url: string) => Promise<IApiResult<T>>;

    /**
     * @param {string} url - Path to append to the base URL.
     * @param {Object} data - The request body.
     * @returns {Promise<IApiResult<T>>} Resolves with an `IApiResult<T>` containing `succeeded`, `error`, and `data`.
     */
    post: <T>(url: string, data: Object) => Promise<IApiResult<T>>;

    /**
     * @param {string} url - Path to append to the base URL.
     * @param {Object} data - The request body.
     * @returns {Promise<IApiResult<T>>} Resolves with an `IApiResult<T>` containing `succeeded`, `error`, and `data`.
     */
    put: <T>(url: string, data: Object) => Promise<IApiResult<T>>;

    /**
     * @param {string} url - Path to append to the base URL.
     * @param {Object} data - The request body.
     * @returns {Promise<IApiResult<T>>} Resolves with an `IApiResult<T>` containing `succeeded`, `error`, and `data`.
     */
    patch: <T>(url: string, data: Object) => Promise<IApiResult<T>>;

    /**
     * @param {string} url - Path to append to the base URL.
     * @returns {Promise<IApiResult<T>>} Resolves with an `IApiResult<T>` containing `succeeded`, `error`, and `data`.
     */
    delete: <T>(url: string) => Promise<IApiResult<T>>;
}

@Injectable({ providedIn: 'root' })
export class Api {
    http = inject(HttpClient);

    clientId = signal<string>('');
    organizationId = signal<string>('');

    constructor() {}

    private requester = (baseUrl: string): IApi => {
        let http = this.http;
        let environmentBaseUrl = environment.apiBaseUrl;
        if (baseUrl.startsWith('http')) {
            environmentBaseUrl = '';
        }
        return {
            get: function <T>(url: string): Promise<IApiResult<T>> {
                return http
                    .get(`${environmentBaseUrl}${baseUrl}${url}`, {
                        withCredentials: true
                    })
                    .toPromise() as Promise<IApiResult<T>>;
            },
            post: function <T>(url: string, data: Object): Promise<IApiResult<T>> {
                return http
                    .post(`${environmentBaseUrl}${baseUrl}${url}`, data, {
                        withCredentials: true
                    })
                    .toPromise() as Promise<IApiResult<T>>;
            },
            put: function <T>(url: string, data: Object): Promise<IApiResult<T>> {
                return http
                    .put(`${environmentBaseUrl}${baseUrl}${url}`, data, {
                        withCredentials: true
                    })
                    .toPromise() as Promise<IApiResult<T>>;
            },
            patch: function <T>(url: string, data: Object): Promise<IApiResult<T>> {
                return http
                    .patch(`${environmentBaseUrl}${baseUrl}${url}`, data, {
                        withCredentials: true
                    })
                    .toPromise() as Promise<IApiResult<T>>;
            },
            delete: function <T>(url: string): Promise<IApiResult<T>> {
                return http
                    .delete(`${environmentBaseUrl}${baseUrl}${url}`, {
                        withCredentials: true
                    })
                    .toPromise() as Promise<IApiResult<T>>;
            }
        };
    };

    /**
     * @param {string} url - The full or partial URL to use as the request base.
     */
    public fromUrl = (url: string): IApi => {
        return this.requester(url);
    };

    /**
     * Returns an `IApi` instance pointing at the `/api` base route.
     * @returns {IApi} Functions for making HTTP requests against `/api`.
     * @example
     * this.api.base().get('/users')
     */
    public base = (): IApi => {
        return this.requester('/api/v2');
    };

    /**
     * Returns an `IApi` instance with the current client ID already embedded in the path (`/api/public/clients/:id`).
     * @returns {IApi} Functions for making HTTP requests scoped to the current client.
     * @example
     * this.api.client().get('/settings')
     */
    public client = (clientId?: string): IApi => {
        if (clientId) {
            return this.requester('/api/v2/public/clients/' + clientId);
        }

        return this.requester('/api/v2/public/clients/' + this.clientId());
    };

    /**
     * Returns the URL string for the public client endpoint, optionally with an appended path.
     * @param {string} [url=''] - Optional path to append after the client ID segment.
     * @returns {string} The resolved URL, e.g. `/api/public/clients/42/some-path`.
     * @example
     * this.api.publicClientUrl('/invoices') // → '/api/public/clients/42/invoices'
     */
    public publicClientUrl = (url: string = ''): string => {
        return `/api/public/clients/${this.clientId()}${url}`;
    };

    /**
     * Returns an `IApi` instance pointing at the `/api/public` route.
     * @returns {IApi} Functions for making HTTP requests against the public API entry point.
     * @example
     * this.api.public().get('/status')
     */
    public public = (): IApi => {
        return this.requester('/api/v2/public');
    };

    /**
     * Returns an `IApi` instance pointing at the public client route (`/api/public/clients/:id`).
     * Equivalent to calling `publicClientUrl()` and passing it to the requester.
     * @returns {IApi} Functions for making HTTP requests scoped to the current client's public endpoint.
     * @example
     * this.api.publicClient().post('/contact', payload)
     */
    public publicClient = (): IApi => {
        return this.requester(this.publicClientUrl());
    };

    /**
     *Returns an `IApi` instance pointing at the admin route (`/api/v2/admin`)
     * @returns {IApi} Functions for making HTTP requests scoped to the admin endpoint.
     */
    public admin = (): IApi => {
        return this.requester('/api/v2/admin');
    };

    /**
     *Returns an `IApi` instance pointing at the superadmin route (`/api/v2/admin/superadmin`)
     * @returns {IApi} Functions for making HTTP requests scoped to the superadmin endpoint.
     */
    public superAdmin = (): IApi => {
        return this.requester('/api/v2/admin/superadmin');
    };
    /**
     * Returns an `IApi` instance pointing at the base organizations route (`/api/v2/organizations`).
     * @returns {IApi} Functions for making HTTP requests scoped to the current organizations endpoint.
     * @example
     * this.api.organization().post('/contact', payload)
     */
    public organization = (): IApi => {
        return this.requester('/api/v2/organizations');
    };

    /**
     * Returns an `IApi` instance pointing at the base organization route (`/api/v2/organization/:id`).
     * Equivalent to calling `publicClientUrl()` and passing it to the requester.
     * @returns {IApi} Functions for making HTTP requests scoped to the current organizations endpoint.
     * @example
     * this.api.organization().post('/contact', payload)
     */
    public currentorganization = (): IApi => {
        return this.requester(`/api/v2/organizations/${this.organizationId() ?? 'test'}`);
    };

    /**
     * Returns an `IApi` instance pointing at the guarded organization route (`/api/v2/organization/:id/super-user`).
     * @returns {IApi} Functions for making HTTP requests scoped to the current organizations superuser endpoint.
     * @example
     * this.api.organizationSuper().post('/contact', payload)
     */
    public organizationSuper = (): IApi => {
        return this.requester('/api/v2/organizations/super-user');
    };

    /**
     * Returns an `IApi` instance pointing at the guarded organizations crm route (`/api/v2/organizations/crm`).
     * @returns {IApi} Functions for making HTTP requests scoped to the current organizations crm endpoint.
     * @example
     * this.api.crm().post('/contact', payload)
     */
    public crm = (): IApi => {
        return this.requester(`/api/v2/organizations/${this.organizationId() ?? ''}/crm`);
    };

    /**
     * Returns an `IApi` instance pointing at the guarded organizations crm route (`/api/v2/organizations/crm`).
     * @returns {IApi} Functions for making HTTP requests scoped to the current organizations crm endpoint.
     * @example
     * this.api.crm().post('/contact', payload)
     */
    public currentOrganizationClient = (clientId?: string): IApi => {
        return this.requester(`/api/v2/organizations/${this.organizationId() ?? ''}/clients/${clientId ? clientId : this.clientId()}`);
    };

    /**
     * Api route for accessing funpay routes
     *
     */
    public funpay = (clientId?: string): IApi => {
        return this.requester(
            `/api/v2/organizations/${this.organizationId() ?? ''}/clients/${clientId ? clientId : this.clientId()}/funpay`
        );
    };
}
